/**
 * Input validation and formatting utilities for Payroll Reports
 */

/**
 * Validate and format hourly rate input
 */
export const handleHourlyRateInputChange = (inputValue, setHourlyRate) => {
  // Remove commas and PHP prefix for processing
  let numericValue = inputValue.replace(/,/g, '').replace('PHP ', '');
  
  // Allow empty input
  if (numericValue === '') {
    setHourlyRate('');
    return;
  }
  
  // Allow typing decimal numbers
  if (/^\d*\.?\d*$/.test(numericValue)) {
    setHourlyRate(numericValue);
  }
};

/**
 * Handle rate multiplier input change with validation
 */
export const handleRateMultiplierInputChange = (category, value, setRateMultipliers) => {
  // Allow empty input or valid decimal numbers
  if (value === '' || /^\d*\.?\d*$/.test(value)) {
    setRateMultipliers((prev) => ({
      ...prev,
      [category]: value
    }));
  }
};

/**
 * Handle additional income input change with formatting
 */
export const handleAdditionalIncomeInputChange = (field, value, setAdditionalIncomes) => {
  // Remove commas and PHP prefix for processing
  let numericValue = value.replace(/,/g, '').replace(/^PHP\s*/, '').trim();
  
  // Allow empty input
  if (numericValue === '') {
    setAdditionalIncomes((prev) => ({
      ...prev,
      [field]: ''
    }));
    return;
  }
  
  // Allow typing decimal numbers (including leading zeros which will be cleaned up)
  if (/^\d*\.?\d*$/.test(numericValue)) {
    // Remove leading zeros but keep the decimal point and zeros after it
    if (numericValue !== '0' && !numericValue.startsWith('0.')) {
      numericValue = numericValue.replace(/^0+/, '');
    }
    setAdditionalIncomes((prev) => ({
      ...prev,
      [field]: numericValue
    }));
  }
};

/**
 * Handle deduction input change with formatting
 */
export const handleDeductionInputChange = (field, value, setDeductions) => {
  // Remove commas and PHP prefix for processing
  let numericValue = value.replace(/,/g, '').replace(/^PHP\s*/, '').trim();
  
  // Allow empty input
  if (numericValue === '') {
    setDeductions((prev) => ({
      ...prev,
      [field]: ''
    }));
    return;
  }
  
  // Allow typing decimal numbers (including leading zeros which will be cleaned up)
  if (/^\d*\.?\d*$/.test(numericValue)) {
    // Remove leading zeros but keep the decimal point and zeros after it
    if (numericValue !== '0' && !numericValue.startsWith('0.')) {
      numericValue = numericValue.replace(/^0+/, '');
    }
    setDeductions((prev) => ({
      ...prev,
      [field]: numericValue
    }));
  }
};

/**
 * Handle custom income entry change
 */
export const handleCustomIncomeEntryChange = (index, field, value, additionalIncomes, setAdditionalIncomes) => {
  setAdditionalIncomes((prev) => {
    const updatedCustomIncomes = [...prev.customIncomes];
    
    if (field === 'amount') {
      // Remove commas and PHP prefix
      let numericValue = value.replace(/,/g, '').replace(/^PHP\s*/, '').trim();
      
      // Allow empty input
      if (numericValue === '') {
        updatedCustomIncomes[index][field] = '';
      } else if (/^\d*\.?\d*$/.test(numericValue)) {
        // Remove leading zeros but keep decimal point
        if (numericValue !== '0' && !numericValue.startsWith('0.')) {
          numericValue = numericValue.replace(/^0+/, '');
        }
        updatedCustomIncomes[index][field] = numericValue;
      }
    } else {
      updatedCustomIncomes[index][field] = value;
    }
    
    return {
      ...prev,
      customIncomes: updatedCustomIncomes
    };
  });
};

/**
 * Handle custom deduction entry change
 */
export const handleCustomDeductionEntryChange = (index, field, value, deductions, setDeductions) => {
  setDeductions((prev) => {
    const updatedCustomDeductions = [...prev.customDeductions];
    
    if (field === 'amount') {
      // Remove commas and PHP prefix
      let numericValue = value.replace(/,/g, '').replace(/^PHP\s*/, '').trim();
      
      // Allow empty input
      if (numericValue === '') {
        updatedCustomDeductions[index][field] = '';
      } else if (/^\d*\.?\d*$/.test(numericValue)) {
        // Remove leading zeros but keep decimal point
        if (numericValue !== '0' && !numericValue.startsWith('0.')) {
          numericValue = numericValue.replace(/^0+/, '');
        }
        updatedCustomDeductions[index][field] = numericValue;
      }
    } else {
      updatedCustomDeductions[index][field] = value;
    }
    
    return {
      ...prev,
      customDeductions: updatedCustomDeductions
    };
  });
};

/**
 * Handle loan balance entry change
 */
export const handleLoanBalanceEntryChange = (index, field, value, deductions, setDeductions) => {
  setDeductions((prev) => {
    const updatedLoanBalances = [...prev.loanBalances];
    updatedLoanBalances[index][field] = value;
    return {
      ...prev,
      loanBalances: updatedLoanBalances
    };
  });
};

/**
 * Handle leave balance entry change
 */
export const handleLeaveBalanceEntryChange = (index, field, value, deductions, setDeductions) => {
  setDeductions((prev) => {
    const updatedLeaveBalances = [...prev.leaveBalances];
    updatedLeaveBalances[index][field] = value;
    return {
      ...prev,
      leaveBalances: updatedLeaveBalances
    };
  });
};

/**
 * Handle additional hours change for manual entries
 */
export const handleAdditionalHoursInputChange = (date, field, value, setManualEntries, getManualEntryForDate) => {
  setManualEntries((prev) => ({
    ...prev,
    [date]: {
      ...getManualEntryForDate(date),
      [field]: value
    }
  }));
};
